import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import SearchInput from "./SearchInput";
import UserSearchResults from "./UserSearchResults";
import { useUserSearch } from "../../hooks/user/useUserSearch";

/**
 * Full-screen search modal for mobile devices
 *
 * Features:
 * - Full-screen overlay
 * - Search input at top
 * - Scrollable results
 * - Close button and backdrop click to dismiss
 */
const UserSearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  // Fetch search results
  const { data, isLoading, isError, error } = useUserSearch(query, {
    limit: 20, // Show more results in modal
  });

  const users = data?.users || [];

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Reset query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
    }
  }, [isOpen]);

  // Handle user click - navigate to profile and close modal
  const handleUserClick = (user) => {
    const userId = user._id || user.id;
    if (userId) {
      navigate(`/users/${userId}`);
      onClose();
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="h-full bg-light-grey flex flex-col">
        {/* Header */}
        <div className="bg-dark-grey shadow-lg">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="text-white hover:text-gold transition-colors p-2 -ml-2"
                aria-label="Close search"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>

              {/* Search Input */}
              <SearchInput
                value={query}
                onChange={setQuery}
                onClear={() => setQuery("")}
                placeholder="Search users..."
                autoFocus={true}
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 py-4">
            <UserSearchResults
              users={users}
              isLoading={isLoading}
              isError={isError}
              error={error}
              query={query}
              compact={false}
              onUserClick={handleUserClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

UserSearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UserSearchModal;
